import { forwardRef } from 'react';

import clsx from 'clsx';

import { TextFieldStyled, useStyles } from './TextArea.styles';
import { TextAreaProps } from './TextArea.types';

/**
 * @component TextArea
 * @description A text area component that lets users input longer amounts of text than a standard text field.
 * TextArea is a multi-line text input control that allows for larger text entries.
 *
 * @designSystem
 * - **Variants**:
 *   - `outlined` (default): Shows a visible border around the input
 *   - `standard`: Shows only a bottom border
 *
 * - **Sizes**:
 *   - `small`: Compact size, useful when space is limited
 *   - `medium`: Standard size for most use cases
 *   - `large`: Expanded size for more visibility
 *
 * - **States**:
 *   - Normal: Default interactive state
 *   - Focused: When the input has focus
 *   - Error: Indicates validation errors or issues
 *   - Disabled: Non-interactive state
 *
 * @usage
 * - Use for comments, feedback, descriptions, or other longer text inputs
 * - Consider setting an appropriate initial height with the `rows` prop
 * - Allow for expansion when users need more space (with auto-expand or scroll)
 * - Use error states to indicate validation issues
 * - Provide clear labels and helper text when necessary
 * - Consider character limits for performance and UX
 *
 * @accessibility
 * - Include descriptive labels for screen readers
 * - Use aria-describedby for helper text or error messages
 * - Ensure keyboard focus states are visible
 * - Provide appropriate validation feedback
 */
export const TextArea = forwardRef<HTMLDivElement, TextAreaProps>(
  ({ variant = 'outlined', size = 'medium', error, disabled, className, ...props }, ref) => {
    const styles = useStyles();

    return (
      <TextFieldStyled
        {...props}
        ref={ref}
        multiline
        variant={variant}
        error={error}
        disabled={disabled}
        className={clsx(
          styles.root,
          error && styles.error,
          disabled && styles.rootDisabled,
          variant === 'outlined' && styles.rootOutlined,
          size === 'small' && styles.rootSmall,
          size === 'medium' && styles.rootMedium,
          size === 'large' && styles.rootLarge,
          className,
        )}
      />
    );
  },
);

TextArea.displayName = 'TextArea';
